import { CATEGORY_OF_NODE_KIND, type CardDef, type NodeKind, type PollutionReason, type StageDef } from "@reagent/sim";

/**
 * §4's fixed hazard-condition → card mapping, centralised here rather than
 * left for each stage to repeat (spec 0007). Exhaustive over PollutionReason
 * so a reason cannot be added to the engine without a card to name here —
 * TypeScript enforces it as a total Record.
 */
export const POLLUTION_CARDS: Record<PollutionReason, string> = {
  overheat: "card-thermal-shock",
  burst: "card-line-leak",
  leak: "card-corrosive-pitting",
  catalystDead: "card-catalyst-deactivation",
  sludge: "card-clogged-line",
  spill: "card-chemical-spill",
  powerOutage: "card-power-outage",
  vermin: "card-rodent-infestation",
  structuralShear: "card-structural-shear",
};

/**
 * Builds a stage's `pollutionCards` from the fixed mapping above for exactly
 * the reasons named — never a data fill-in, so a caller still chooses which
 * hazards a stage teaches. Spec 0007 open question 11 ("pollutionCards stays
 * optional and per-stage") is overturned by spec 0029: it is still per-stage,
 * but a stage's choice is now a floor, not a free one — `validateStage`
 * rejects any stage whose call here omits a reason `reachablePollutionReasons`
 * says that stage can trigger. Naming an unreachable reason too is still fine.
 */
export function pollutionCardsFor(
  ...reasons: readonly PollutionReason[]
): Partial<Record<PollutionReason, string>> {
  const result: Partial<Record<PollutionReason, string>> = {};
  for (const reason of reasons) result[reason] = POLLUTION_CARDS[reason];
  return result;
}

/** The card ids a stage puts within a player's reach: its starting deck, plus whatever Phase 1's setupCatalogue sells. */
function offeredCardIds(stage: StageDef): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const instance of stage.startingDeck) ids.add(instance.cardId);
  for (const offer of stage.setupCatalogue ?? []) ids.add(offer.cardId);
  return ids;
}

/** True when some node the stage authors, or some card it offers, deploys a node kind `predicate` accepts. */
function authorsOrDeploys(stage: StageDef, cards: readonly CardDef[], predicate: (kind: NodeKind) => boolean): boolean {
  if (stage.nodes.some((n) => predicate(n.kind))) return true;

  // "Deployable" (Schema Impact) means a setupCatalogue card's deploysNode —
  // a hardware card sitting in the starting deck with nowhere placed for it
  // yet does not count until this spec's stages actually offer one that way.
  const cardsById = new Map(cards.map((c) => [c.id, c]));
  return (stage.setupCatalogue ?? []).some((offer) => {
    const kind = cardsById.get(offer.cardId)?.deploysNode;
    return kind !== undefined && predicate(kind);
  });
}

/**
 * Over-approximates: may include a reason that never fires; must never omit
 * one that can. Follows the engine's `pollute()` call sites (`hazards.ts`,
 * `facility.ts`, `movement.ts`, `cards.ts`) — see
 * `docs/specs/implemented/0029-curriculum-table.md`'s Schema Impact table for
 * the predicate each row below implements.
 */
export function reachablePollutionReasons(stage: StageDef, cards: readonly CardDef[]): ReadonlySet<PollutionReason> {
  const offered = offeredCardIds(stage);
  const cardsById = new Map(cards.map((c) => [c.id, c]));
  const offeredCards = [...offered].map((id) => cardsById.get(id)).filter((c): c is CardDef => c !== undefined);

  const hasEdge = stage.edges.length > 0;
  const hasGlassEdge = stage.edges.some((e) => (e.material ?? "glass") === "glass");
  const offersAnyPipeCard = offeredCards.some((c) => c.deploysPipe !== undefined);
  const offersGlassPipeCard = offeredCards.some((c) => c.deploysPipe === "glass");

  const scriptsEvent = (kind: "heatwave" | "seismic" | "powerSurge"): boolean =>
    (stage.scriptedEvents ?? []).some((e) => e.kind === kind);

  const burst = hasEdge || offersAnyPipeCard;
  const overheat = stage.recipes.some((r) => r.heat > 0) || scriptsEvent("heatwave");
  // A loaded move or dismantle in Play contaminates a tile (spec 0067), whatever the rig.
  const spill = burst || overheat || stage.liveBuild !== undefined;
  const structuralShear = scriptsEvent("heatwave") || scriptsEvent("seismic");
  const leak = (hasGlassEdge || offersGlassPipeCard) && (stage.corrosiveSpecies.length > 0 || spill || structuralShear);
  const sludge = authorsOrDeploys(stage, cards, (k) => {
    const category = CATEGORY_OF_NODE_KIND[k];
    return category !== null && category !== "flowMoverBuffer";
  });
  const catalystDead = authorsOrDeploys(stage, cards, (k) => k === "catalyticBed");
  // Spec 0033: vermin infests any infestable equipment (CATEGORY_OF_NODE_KIND[k] !== null).
  const vermin = authorsOrDeploys(stage, cards, (k) => CATEGORY_OF_NODE_KIND[k] !== null);
  const powerOutage = scriptsEvent("powerSurge");

  const reachable = new Set<PollutionReason>();
  if (burst) reachable.add("burst");
  if (spill) reachable.add("spill");
  if (leak) reachable.add("leak");
  if (overheat) reachable.add("overheat");
  if (sludge) reachable.add("sludge");
  if (catalystDead) reachable.add("catalystDead");
  if (vermin) reachable.add("vermin");
  if (powerOutage) reachable.add("powerOutage");
  if (structuralShear) reachable.add("structuralShear");
  return reachable;
}
